import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { Upload, Save, ArrowLeft, Loader2, Trash2 } from "lucide-react";
import {
  AddBlogsThunk,
  GetSingleBlogsThunk,
  UpdateBlogsThunk,
  AllBlogsCategoryThunk,
} from "../../redux/states/blogs/thunk";
import RichTextEditor from "../../components/common/RichTextEditor";

const CreateUpdateBlogs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleBlog, category, loading, error } = useSelector(
    (state) => state.blogs
  );
  const API_URL = process.env.REACT_APP_API_URL;

  const [formData, setFormData] = useState({
    category_id: "2",
    name: "",
    slug: "",
    short_description: "",
    long_description: "",
    description: "",
    sort_by: "1",
    tags: "",
    meta_title: "",
    meta_keyword: "",
    meta_description: "",
    meta_schema: "",
    status: "active",
    views: "1",
    image: null,
  });

  const [preview, setPreview] = useState(null);

  useEffect(() => {
    dispatch(AllBlogsCategoryThunk());
  }, [dispatch]);

  useEffect(() => {
    if (uuid) {
      dispatch(GetSingleBlogsThunk(uuid));
    }
  }, [uuid, dispatch]);

  useEffect(() => {
    if (singleBlog) {
      const updatedFormData = {
        name: singleBlog.name || "",
        slug: singleBlog.slug || "",
        short_description: singleBlog.short_description || "",
        long_description: singleBlog.long_description || "",
        description: singleBlog.description || "",
        sort_by: singleBlog.sort_by || "",
        tags: singleBlog.tags || "",
        meta_title: singleBlog.meta_title || "",
        meta_keyword: singleBlog.meta_keyword || "",
        meta_description: singleBlog.meta_description || "",
        meta_schema: singleBlog.meta_schema || "",
        status: singleBlog.status || "",
        views: singleBlog.views || "",
        image: null,
      };

      setFormData(updatedFormData);

      if (singleBlog.image) {
        setPreview(
          singleBlog.image.startsWith("http")
            ? singleBlog.image
            : `${process.env.REACT_APP_API_URL}/${singleBlog.image}`
        );
      }
    }
  }, [singleBlog, API_URL]);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files && files[0]) {
      setFormData({ ...formData, [name]: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else if (name === "name") {
      setFormData({
        ...formData,
        name: value,
        slug: generateSlug(value),
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleQuillChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.slug || !formData.category_id) {
      alert("Please fill all required fields");
      return;
    }

    const data = new FormData();

    // Append all form data
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null && formData[key] !== undefined) {
        data.append(key, formData[key]);
      }
    });

    if (uuid) {
      console.log("Updating blog with uuid:", uuid);
      dispatch(UpdateBlogsThunk({ uuid, formData: data })).then((result) => {
        if (result && !result.error) {
          navigate("/blogs");
        }
      });
    } else {
      dispatch(AddBlogsThunk(data)).then((result) => {
        if (result && !result.error) {
          navigate("/blogs");
        }
      });
    }
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-12xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-2">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-sm font-bold text-gray-800">
                  {uuid ? "Update Blog" : "Create New Blog"}
                </h1>
                <p className="text-gray-600 mt-2">
                  {uuid ? "Modify the blog details" : "Add a new blog post"}
                </p>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600 border border-gray-300 px-4 py-2 rounded-lg"
              >
                <ArrowLeft className="w-4 h-4" /> Go Back
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Image Upload Section */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <h2 className="text-xl font-semibold mb-2 border-b pb-2">
                Blog Image
              </h2>
              <div className="text-center">
                {preview ? (
                  <div className="relative inline-block">
                    <img
                      src={preview}
                      alt="preview"
                      className="w-64 h-48 rounded-xl object-cover border"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, image: null });
                        setPreview(null);
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl py-10 cursor-pointer bg-gray-50 hover:bg-gray-100 transition-all">
                    <Upload className="w-8 h-8 mb-2 text-blue-600" />
                    <span className="text-gray-600">Upload Blog Image</span>
                    <span className="text-sm text-gray-400 mt-1">
                      PNG, JPG, JPEG up to 5MB
                    </span>
                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Category & Basic Fields */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <h2 className="text-xl font-semibold mb-4 border-b pb-2">
                Blog Information
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="font-medium text-gray-700">
                    Blog Title *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full border rounded-lg px-4 py-2 mt-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    placeholder="Enter blog title"
                  />
                </div>

                <div>
                  <label className="font-medium text-gray-700">Slug *</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    required
                    className="w-full border rounded-lg px-4 py-2 mt-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    placeholder="blog-url-slug"
                  />
                </div>

                <div>
                  <label className="font-medium text-gray-700">
                    Category *
                  </label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    required
                    className="w-full border rounded-lg px-4 py-2 mt-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  >
                    <option value="">Select Category</option>
                    {category
                      ?.filter((cat) => cat.status === "active")
                      .map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="font-medium text-gray-700">
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-4 py-2 mt-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    placeholder="tech, react, web"
                  />
                </div>
              </div>
            </div>

            {/* Meta Info */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <h2 className="text-xl font-semibold mb-4 border-b pb-2">
                SEO Meta Information
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="font-medium text-gray-700">
                    Meta Title
                  </label>
                  <input
                    type="text"
                    name="meta_title"
                    value={formData.meta_title}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-4 py-2 mt-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="font-medium text-gray-700">
                    Meta Keywords
                  </label>
                  <input
                    type="text"
                    name="meta_keyword"
                    value={formData.meta_keyword}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-4 py-2 mt-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="font-medium text-gray-700">
                    Meta Description
                  </label>
                  <textarea
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={handleChange}
                    rows="3"
                    className="w-full border rounded-lg px-4 py-2 mt-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="font-medium text-gray-700">
                    Meta Schema
                  </label>
                  <textarea
                    name="meta_schema"
                    value={formData.meta_schema}
                    onChange={handleChange}
                    rows="3"
                    className="w-full border rounded-lg px-4 py-2 mt-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Descriptions */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <h2 className="text-xl font-semibold mb-4 border-b pb-2">
                Blog Content
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="font-medium text-gray-700 block mb-2">
                    Short Description
                  </label>
                  <RichTextEditor
                    value={formData.short_description}
                    onChange={(value) =>
                      handleQuillChange("short_description", value)
                    }
                  />
                </div>

                <div>
                  <label className="font-medium text-gray-700 block mb-2">
                    Long Description
                  </label>
                  <RichTextEditor
                    value={formData.long_description}
                    onChange={(value) =>
                      handleQuillChange("long_description", value)
                    }
                  />
                </div>

                <div>
                  <label className="font-medium text-gray-700 block mb-2">
                    Description
                  </label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(value) =>
                      handleQuillChange("description", value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <h2 className="text-xl font-semibold mb-4 border-b pb-2">
                Status
              </h2>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="status"
                    value="active"
                    checked={formData.status === "active"}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  Active
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="status"
                    value="inactive"
                    checked={formData.status === "inactive"}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  Inactive
                </label>
              </div>
            </div>

            {/* Submit */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-4 rounded-xl flex justify-center items-center gap-2 transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {uuid ? "Update Blog" : "Save Blog"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </NonAuthLayout>
  );
};

export default CreateUpdateBlogs;
